import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowUpRight, User as UserIcon, LogOut, ShoppingCart } from "lucide-react";
import { useAuth } from "@/auth";
import { useCart } from "@/cart";

const links = [
  { to: "/", label: "Overview", testId: "nav-link-overview" },
  { to: "/faq", label: "FAQ", testId: "nav-link-faq" },
  { to: "/contact", label: "Contact", testId: "nav-link-contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();

  return (
    <header data-testid="site-navbar" className="fixed top-0 left-0 right-0 z-40 border-b border-neutral-200 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="font-display text-xl font-extrabold tracking-tight">
          DISJINX<span className="text-neutral-400">.</span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-neutral-950" : "text-neutral-500 hover:text-neutral-950"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {user ? (
            <>
              {user.is_admin && (
                <NavLink
                  to="/admin"
                  data-testid="nav-link-admin"
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors duration-200 ${
                      isActive ? "text-neutral-950" : "text-neutral-500 hover:text-neutral-950"
                    }`
                  }
                >
                  Admin
                </NavLink>
              )}
              <NavLink
                to="/account"
                data-testid="nav-link-account"
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "text-neutral-950" : "text-neutral-500 hover:text-neutral-950"
                  }`
                }
              >
                <UserIcon className="h-4 w-4" />
                My Billing
              </NavLink>
              <button
                data-testid="nav-logout-button"
                onClick={logout}
                aria-label="Sign out"
                className="text-neutral-500 transition-colors duration-200 hover:text-neutral-950"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <Link
              to="/signin"
              data-testid="nav-signin-button"
              className="text-sm font-medium text-neutral-500 transition-colors duration-200 hover:text-neutral-950"
            >
              Sign in
            </Link>
          )}
          <button
            data-testid="nav-cart-button"
            onClick={() => setCartOpen(true)}
            aria-label="Open cart"
            className="relative text-neutral-950 transition-colors duration-200 hover:text-neutral-600"
          >
            <ShoppingCart className="h-5 w-5" strokeWidth={1.75} />
            {count > 0 && (
              <span data-testid="nav-cart-badge" className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-950 text-[9px] font-bold text-white">
                {count}
              </span>
            )}
          </button>
          <Link
            to="/contact"
            data-testid="nav-cta-button"
            className="group inline-flex items-center gap-1.5 bg-neutral-950 px-5 py-2.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-neutral-800"
          >
            Start a conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="border-t border-neutral-200 bg-white px-6 py-6 md:hidden">
          <div className="flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-neutral-700"
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <NavLink
                to="/account"
                data-testid="nav-link-account-mobile"
                onClick={() => setOpen(false)}
                className="text-base font-medium text-neutral-700"
              >
                My Billing
              </NavLink>
            ) : (
              <NavLink
                to="/signin"
                data-testid="nav-signin-button-mobile"
                onClick={() => setOpen(false)}
                className="w-fit text-base font-medium text-neutral-700"
              >
                Sign in
              </NavLink>
            )}
            <button
              data-testid="nav-cart-button-mobile"
              onClick={() => { setOpen(false); setCartOpen(true); }}
              className="w-fit text-base font-medium text-neutral-700"
            >
              Cart{count > 0 ? ` (${count})` : ""}
            </button>
            <Link
              to="/contact"
              data-testid="nav-cta-button-mobile"
              onClick={() => setOpen(false)}
              className="inline-flex w-fit items-center gap-1.5 bg-neutral-950 px-5 py-2.5 text-sm font-semibold text-white"
            >
              Start a conversation
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
